import type { IterableLike } from "../types/IterableLike"
import { IsArray, IsNull, Values } from "@pdfking/tfunc"
import { is_element } from "./is_element"
import { is_function } from "./is_function"
import { is_iterator } from "./is_iterator"
import { is_mapping } from "./is_mapping"

/**
 * Converts value(s) to array.
 *
 * Handles arrays, objects, iterators, primitives, null/undefined.
 * Multiple args are flattened into single array.
 *
 * @param args - Value(s) to convert
 * @returns Array
 *
 * @example
 * ```ts
 * import { to_array } from "@pdfking/rfunc"
 *
 * to_array([1, 2]) // [1, 2]
 * to_array({ a: 1, b: 2 }) // [1, 2]
 * to_array(5) // [5]
 * to_array(null) // []
 * to_array(1, 2, 3) // [1, 2, 3]
 * ```
 */
const _to_array = <T>(iterable: IterableLike<T>): T[] => {
  if (IsArray(iterable)) {
    return iterable
  }
  if (is_iterator(iterable)) {
    const array: T[] = []
    for (const value of iterable) {
      array.push(value as T)
    }
    return array
  }
  if (is_function(iterable) || is_element(iterable)) {
    return [iterable as T]
  }
  if (is_mapping(iterable)) {
    return Values(iterable) as T[]
  }
  if (IsNull(iterable)) {
    return []
  }
  return [iterable as T]
}

export const to_array = <T>(...args: IterableLike<T>[]): T[] => {
  if (args.length == 1) {
    return _to_array(args[0])
  }
  return _to_array(args).reduce<T[]>(
    (a, b) => a.concat(_to_array(b as IterableLike<T>)),
    []
  )
}
